// Parser de DACTE (Documento Auxiliar do CT-e). CTe rodoviario costuma trazer
// pedagio destacado como componente do valor da prestacao - precisamos extrair
// pra preencher F1_VALPEDG na inclusao manual (mesma logica que ja existe em
// notasPendentes.tlpp na aprovacao de lote).
//
// Padrao observado nos 3 modelos de teste (CTE 117250 / 73626 / 7968): a label
// "PEDAGIO" (ou "Pedagio") aparece com o valor numerico imediatamente em
// sequencia, separados por espacos - pdf.js junta items no mesmo y na mesma
// linha. Regex `/PED[AÁ]GIO\s+([\d.,]+)/i` cobre os 3.
//
// Layout "grade" (ex.: DACTE West Cargo): a tabela "Componentes do Valor da
// Prestacao" desenha 3 colunas de NOME/VALOR lado a lado - label e valor do
// PEDAGIO caem em linhas separadas na extracao (nao ficam a 2px um do
// outro no eixo Y). extractPedagioGrade cobre esse caso via casamento
// posicional entre o bloco de labels e o bloco de valores.
//
// Valores podem vir em formato US (763.65) ou BR (288,00) dependendo da
// transportadora - parseMoney trata ambos.

import { NfseData, parseMoney } from './nfse-parser';
import { SIGLA_ALT, extractProcesso } from './regex-processo';

// Detecta se o texto eh de um CTe (DACTE - transporte). Procura marcadores
// estruturais do documento.
export function isCte(text: string): boolean {
  const upper = text.toUpperCase();
  // "CT-E" eh o codigo nacional; outros marcadores comuns no DACTE.
  return upper.includes('DACTE') ||
         upper.includes('CT-E') ||
         upper.includes('CONHECIMENTO DE TRANSPORTE ELETR') ||
         upper.includes('COMPONENTES DO VALOR DA PRESTA');
}

// Captura primeiro CNPJ no formato XX.XXX.XXX/XXXX-XX. Retorna so digitos.
function extractFirstCnpj(text: string): string {
  const m = text.match(/\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/);
  return m ? m[0].replace(/\D/g, '') : '';
}

// CNPJ do tomador: em DACTEs as palavras "TOMADOR" / "DESTINATARIO"
// aparecem 2x ou mais (uma na header table, outra na secao de dados).
// Usa matchAll e pega a ULTIMA ocorrencia - tipicamente eh a secao
// de dados onde o CNPJ verdadeiro do tomador esta proximo.
function extractTomadorCnpj(text: string): string {
  const re = /(?:TOMADOR|DESTINAT[AÁ]RIO|RECEBEDOR)[\s\S]{0,400}?(\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2})/gi;
  const matches = [...text.matchAll(re)];
  if (matches.length === 0) { return ''; }
  return matches[matches.length - 1][1].replace(/\D/g, '');
}

// Whitelist dos labels reais da tabela "Componentes do Valor da Prestacao"
// do DACTE (vocabulario fixo do layout SEFAZ). Usado so pelo fallback de
// grade (ver extractPedagio) - evita que fragmentos de OUTROS cabecalhos
// da pagina (ex.: "A RECEBER", resto de "VALOR A RECEBER") sejam
// confundidos com um label de componente e desloquem o indice do PEDAGIO
// dentro do bloco.
const COMPONENT_LABELS =
  /^(FRETE\s+PESO|FRETE\s+VALOR|PED[AÁ]GIO|GRIS|SEGURO|DESPACHO|DESCARREGAMENTO|ESCOLTA|ESTACIONAMENTO|ARMAZENAGEM|DI[AÁ]RIA|OUTROS|ADEME|RECIBO|TDE|TDA|TRT|DTA|ICMS|SUFRAMA|PERICULOSIDADE)$/i;

// Extrai o valor do pedagio quando o label PEDAGIO e o valor caem em
// linhas separadas na extracao do pdf.js - layout "grade" onde a tabela
// desenha todos os labels da linha visual antes de desenhar todos os
// valores (ex.: DACTE West Cargo, 3 colunas de NOME/VALOR repetidas).
// Localiza o indice do label PEDAGIO dentro do bloco de labels
// contiguos (usando a whitelist acima) e pega o valor de MESMO indice
// no bloco de valores que vem logo em seguida.
function extractPedagioGrade(text: string): number {
  const lines = text.split('\n').map(l => l.trim().replace(/\s+/g, ' '));
  const NOISE = /^(NOME|VALOR)$/i;
  const isLabelLine = (l: string) => COMPONENT_LABELS.test(l);
  const isValueLine = (l: string) => !!l && /^[\d.,]+$/.test(l);

  for (let i = 0; i < lines.length; i++) {
    if (!/^PED[AÁ]GIO$/i.test(lines[i])) continue;

    let start = i;
    while (start > 0 && (isLabelLine(lines[start - 1]) || NOISE.test(lines[start - 1]))) {
      start--;
    }
    let end = i;
    while (end + 1 < lines.length && (isLabelLine(lines[end + 1]) || NOISE.test(lines[end + 1]))) {
      end++;
    }

    const labelBlock = lines.slice(start, end + 1).filter(l => !NOISE.test(l));
    const idx = labelBlock.findIndex(l => /^PED[AÁ]GIO$/i.test(l));
    if (idx < 0) continue;

    const valueBlock: string[] = [];
    for (let j = end + 1; j < lines.length && valueBlock.length < labelBlock.length; j++) {
      if (isValueLine(lines[j])) { valueBlock.push(lines[j]); continue; }
      if (NOISE.test(lines[j]) || lines[j] === '') continue;
      break;
    }

    if (valueBlock[idx]) {
      const v = parseMoney(valueBlock[idx]);
      if (v > 0) return v;
    }
  }
  return 0;
}

// Extrai o valor do pedagio. Procura a label PEDAGIO/Pedagio e captura o
// proximo numero. Tolera variantes "PEDAGIO" / "Pedagio" / "PEDÁGIO".
//
// Dois caminhos:
//   1. Inline (modelos originais CTE 117250/73626/7968): label e valor na
//      MESMA linha extraida pelo pdf.js.
//   2. Grade (ex.: DACTE West Cargo): label e valor em linhas separadas -
//      ver extractPedagioGrade. So tenta quando o inline nao acha nada,
//      preservando o comportamento ja validado dos 3 modelos originais.
function extractPedagio(text: string): number {
  const m = text.match(/PED[AÁ]GIO\s+([\d.,]+)/i);
  if (m) {
    const v = parseMoney(m[1]);
    if (v > 0) return v;
  }
  return extractPedagioGrade(text);
}

// Valor total da CTe. Estrategia tolerante a layouts diferentes:
//   1. "Valor a Receber" antes do valor (mais comum)
//   2. Valor antes da label "VALOR A RECEBER" (raro, ex.: CTE 7968)
//   3. Fallback: valor BR-formatado que aparece 2+ vezes no texto (total
//      eh espelhado entre "Valor Total do Servico" e "Valor a Receber" -
//      duplica enquanto componentes individuais aparecem uma so vez)
//
// Evita capturar "Valor Total da Carga" (que pode ser maior que o total
// do servico) e o primeiro numero apos labels de cabecalho (que geralmente
// eh o frete, nao o total).
function extractValorTotal(text: string): number {
  // Padrao 1: label antes do valor (CTE 117250, 73626) - exige decimal BR
  // pra nao capturar numeros pequenos sem casas decimais que aparecem em
  // labels secundarios proximos (ex.: aliquota "80").
  const m1 = text.match(/VALOR\s+A\s+RECEBER[\s\S]{0,40}?(\d[\d.]*,\d{2})/i);
  if (m1) {
    const v = parseMoney(m1[1]);
    if (v > 0) { return v; }
  }
  // Padrao 2: valor na linha IMEDIATAMENTE acima da label (CTE 7968).
  // Restringe a faixa entre valor e label pra so aceitar whitespace
  // (sem outras linhas no meio) - evita capturar numeros de componentes
  // que aparecem varias linhas antes do label.
  const m2 = text.match(/(\d[\d.]*,\d{2})\s*\n[ \t]*VALOR\s+A\s+RECEBER/i);
  if (m2) {
    const v = parseMoney(m2[1]);
    if (v > 0) { return v; }
  }
  // Fallback: valor BR-formatado que aparece 2+ vezes
  const allBr = text.match(/\d[\d.]*,\d{2}/g) || [];
  const counts = new Map<string, number>();
  for (const s of allBr) { counts.set(s, (counts.get(s) || 0) + 1); }
  const dup = [...counts.entries()].filter(([, n]) => n >= 2).map(([k]) => parseMoney(k));
  if (dup.length > 0) {
    // Maior dentre os duplicados - excluir valores absurdamente altos
    // (ex.: Valor Total da Carga = R$ 1.639.068,78) usando teto razoavel
    // de 1 milhao pra valor de servico de transporte.
    const filtered = dup.filter(n => n < 1_000_000);
    if (filtered.length > 0) { return Math.max(...filtered); }
  }
  return 0;
}

// Numero do CTe. DACTEs trazem "Numero" / "Nº" / "Número CT-e" em geral.
function extractNumero(text: string, fileName?: string): string {
  // Padrao 1: "Numero" ou "Número" seguido de digitos
  const m1 = text.match(/N[uúº]?\s*mero\s*(?:CT-?e)?\s*[:\n]?\s*(\d{4,12})/i);
  if (m1) return m1[1];
  // Padrao 2: filename "CTE NNNNN" ou "CTE NNNNN.pdf"
  if (fileName) {
    const fm = fileName.match(/CTE\s*0*(\d{3,12})/i);
    if (fm) return fm[1];
  }
  return '';
}

// Detecta se o emitente eh Simples Nacional. Heuristica de texto: "Simples
// Nacional" aparece em CTes emitidos por transportadoras desse regime
// (geralmente proximo ao CRT ou no rodape). A leitura ideal seria a tag XML
// <indSN>1</indSN>, mas o PDF nao expoe XML embarcado nos exemplos testados.
function detectSimplesNacional(text: string): boolean {
  // Case-insensitive; ignora variacoes de espacamento
  return /simples\s+nacional/i.test(text);
}

// ICMS isento: le a "Situacao Tributaria" do DACTE. Padroes reais confirmados
// em PDFs (07/2026):
//   "40 - ICMS isenção"        (CST 40 - isenta)
//   "41 - ICMS não tributada"  (CST 41 - nao tributada)
//   "51 - ICMS diferido"       (CST 51 - diferido, sem destaque)
// vs. nao-isentos: "00 - Tributada Integralmente", "90 - ICMS Simples Nacional".
// Casa o CODIGO + a descricao juntos pra nao pegar um "40"/"41" solto de outra
// coluna (valor, base). Acento-tolerante (o texto do PDF varia). Este parser e
// heuristico: o backend continua sendo a fonte final via <ICMS45> do XML quando
// houver; aqui e o unico sinal disponivel na inclusao MANUAL (so PDF).
function detectIcmsIsento(text: string): boolean {
  const t = text.normalize('NFD').replace(/[̀-ͯ]/g, '');
  return /\b4\s*0\s*[-–]\s*ICMS\s*isen/i.test(t)          // 40 - ICMS isencao
      || /\b4\s*1\s*[-–]\s*ICMS\s*na?o\s*tribut/i.test(t) // 41 - ICMS nao tributada
      || /\b5\s*1\s*[-–]\s*ICMS\s*difer/i.test(t);        // 51 - ICMS diferido
}

// ---- Chave de acesso do CT-e (44 digitos) -------------------------------
// Impressa no DACTE como "CHAVE DE ACESSO" seguida de 44 digitos (agrupados
// de 4). Alguns layouts nao colocam o numero adjacente ao label - por isso o
// fallback varre qualquer run de 44 digitos com DV mod-11 valido e modelo 57
// (CT-e). O DV evita capturar um numero de 44 digitos aleatorio.
//
// Layout (44): cUF(2) AAMM(4) CNPJ(14) mod(2) serie(3) nCT(9) tpEmis(1)
//              cCT(8) cDV(1). Serie (dig 23-25) e numero (dig 26-34) saem daqui.

// Digito verificador mod-11 sobre os 43 primeiros digitos.
function chaveDV(ch43: string): string {
  let peso = 2;
  let soma = 0;
  for (let i = ch43.length - 1; i >= 0; i--) {
    soma += parseInt(ch43[i], 10) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  const resto = soma % 11;
  const dv = (resto === 0 || resto === 1) ? 0 : 11 - resto;
  return String(dv);
}

// Valida chave de 44 digitos: tamanho + DV. Exportada para o front reusar
// no feedback do campo (mostrar se a chave esta correta). Nao exige mod 57
// aqui para o usuario poder digitar/corrigir sem falso-negativo.
export function isChaveDacteValida(chave: string): boolean {
  const d = (chave || '').replace(/\D/g, '');
  return d.length === 44 && chaveDV(d.slice(0, 43)) === d[43];
}

// Serie (3 digitos) embutida na chave, sem zeros a esquerda ("010" -> "10").
export function serieFromChave(chave: string): string {
  const d = (chave || '').replace(/\D/g, '');
  return d.length === 44 ? String(parseInt(d.slice(22, 25), 10)) : '';
}

// Numero do CT-e (9 digitos) embutido na chave, sem zeros a esquerda.
export function numeroFromChave(chave: string): string {
  const d = (chave || '').replace(/\D/g, '');
  return d.length === 44 ? String(parseInt(d.slice(25, 34), 10)) : '';
}

// Extrai a chave de acesso do texto do DACTE. Retorna 44 digitos ou "".
function extractChaveDacte(text: string): string {
  const flat = text.replace(/[\r\n]+/g, ' ');
  // 1. adjacente ao label "CHAVE DE ACESSO"
  const m = flat.match(/CHAVE\s+DE\s+ACESSO[^0-9]*((?:\d[\s.]*){44,60})/i);
  if (m) {
    const d = m[1].replace(/\D/g, '').slice(0, 44);
    if (d.length === 44 && chaveDV(d.slice(0, 43)) === d[43]) { return d; }
  }
  // 2. fallback: qualquer run de 44 digitos com DV valido e modelo 57 (CTe)
  const cands = flat.match(/(?:\d{4}[\s.]?){10,11}\d{1,4}|\d{40,48}/g) || [];
  for (const c of cands) {
    const d = c.replace(/\D/g, '');
    if (d.length === 44 && d.slice(20, 22) === '57' && chaveDV(d.slice(0, 43)) === d[43]) { return d; }
  }
  return '';
}

// Processo (referencia "SIGLA NNNN/AA") do CTe. Sai das observacoes do DACTE
// ("... PROCESSO ACERO 0183/25") ou, na falta, do nome do arquivo, que o
// operador costuma nomear pelo processo ("ACESC 0274-26 - DACTE 16836.PDF").
//
// Mais restrito que o extractProcesso aplicado ao texto inteiro, que no DACTE
// da falso positivo: "PR" e sigla de processo E UF, e o \s do regex atravessa
// a quebra de linha ("SAO JOSE DOS PINHAIS PR" + CEP da linha seguinte
// virava "PR 0413/35" no DACTE 750103).
// Por isso a busca e por linha e a sigla nao pode estar colada em letra
// (o "AC" de "PLACA" nao conta).
const INICIO_PROCESSO = new RegExp(`(?<![A-Z])(?:${SIGLA_ALT})[ ]?\\d`, 'gi');

export function extractProcessoCte(text: string, fileName?: string): string {
  for (const linha of text.split('\n')) {
    for (const m of linha.matchAll(INICIO_PROCESSO)) {
      const proc = extractProcesso(linha.substring(m.index ?? 0));
      if (proc) { return proc; }
    }
  }
  if (fileName) {
    // "0274-26", "1567_26" e "1564.26" no nome do arquivo = "0274/26".
    const nome = fileName.replace(/\.pdf$/i, '').replace(/(\d{4})[-_.](\d{2})(?!\d)/g, '$1/$2');
    return extractProcesso(nome);
  }
  return '';
}

// API publica: parseia texto de CTe e devolve NfseData populado com valorPedagio.
export function parseCte(text: string, fileName?: string): NfseData {
  const chave = extractChaveDacte(text);
  const numeroChave = numeroFromChave(chave);
  return {
    cnpjEmissor: extractFirstCnpj(text),
    cnpjTomador: extractTomadorCnpj(text),
    // Numero e serie: quando a chave foi extraida, sao autoritativos (vem
    // embutidos nela). Senao cai no numero por texto/filename e serie vazia
    // (o usuario preenche na tela - ambos obrigatorios no CTe).
    numeroNf:    numeroChave || extractNumero(text, fileName),
    serie:       serieFromChave(chave),
    chaveAcesso: chave,
    valorTotal:  extractValorTotal(text),
    valorPedagio: extractPedagio(text),
    simplesNacional: detectSimplesNacional(text),
    icmsIsento: detectIcmsIsento(text),
    processo: extractProcessoCte(text, fileName),
    retencoes: { ir: 0, pis: 0, cofins: 0, csll: 0, inss: 0, iss: 0 },
    source: 'cte'
  };
}
